import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api from "api";
import constants from "utils/constants";
import { updateCurrentUser, useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import SettingActions from "components/lib/SettingActions";

import TermsCheckbox from "./TermsCheckbox";

const LOGIN_SERVICE_HOST = /^login(-[a-z0-9]+)?\.simplydo\.co\.uk$/;
const LOGIN_COMPLETE_PATH =
  /^\/auth\/(saml|oauth\/[a-z0-9]+)\/login\/complete$/i;

// `next` comes from the URL and can be set by anyone, so only follow it to our
// own login service's completion endpoint. Anything else (other hosts,
// javascript: URLs) falls back to the home page.
function getSafeReturnUrl(next: string): string {
  let url: URL;
  try {
    url = new URL(atob(next), window.location.origin);
  } catch {
    return "/";
  }

  const trustedHosts = new Set([window.location.host]);
  const configuredLoginService = import.meta.env.VITE_LOGIN_SERVICE;
  if (configuredLoginService) {
    try {
      trustedHosts.add(new URL(configuredLoginService).host);
    } catch {
      // Ignore a malformed env value and fall back to the defaults
    }
  }

  const isTrustedHost =
    trustedHosts.has(url.host) || LOGIN_SERVICE_HOST.test(url.hostname);
  const isTrustedProtocol =
    url.protocol === "https:" ||
    (url.protocol === "http:" && trustedHosts.has(url.host));

  if (
    !isTrustedHost ||
    !isTrustedProtocol ||
    !LOGIN_COMPLETE_PATH.test(url.pathname)
  ) {
    return "/";
  }
  return url.toString();
}

const AgreeToTerms = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const returnUrl = useMemo(
    () => getSafeReturnUrl(searchParams.get("next") || ""),
    [searchParams],
  );

  const [hasAgreed, setHasAgreed] = useState(false);
  const [agreedCustomTerms, setAgreedCustomTerms] = useState(false);
  const [organisation, setOrganisation] = useState<
    OpenAPI.Schemas["Organisation"] | null
  >(user?.ownerOrganisation ?? null);

  const [loading, setLoading] = useState(false);

  const requiresTermsAgreement = !hasAgreed;
  const requiresCustomTermsAgreement = organisation?.customTerms?.enabled
    ? !agreedCustomTerms
    : false;

  useEffect(() => {
    if (!organisation) {
      const code = util.code();
      if (!code) {
        setError("Something went wrong. Please try logging in again.");
        return;
      }
      api.organisations.getByCode(
        code,
        (org) => {
          setOrganisation(org);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    }
  }, [organisation]);

  const onAgree = useCallback(() => {
    if (!hasAgreed) {
      toast.error("Please agree to the terms before continuing");
      return;
    }
    setLoading(true);
    if (user) {
      api.auth.agreeToTerms(
        constants.termsVersion,
        user?.ownerOrganisation?.customTerms?.version ?? "",
        (...args: unknown[]) => {
          const legalAgreements = args[0] as typeof user.legalAgreements;
          updateCurrentUser({ legalAgreements });
          setLoading(false);
          api.invalidateQueries(api.users.me);
        },
        (err) => {
          toast.error(err.message);
          setLoading(false);
        },
      );
    } else {
      try {
        window.location.href = returnUrl;
      } catch (e) {
        toast.error("Something went wrong. Please try logging in again.");
      }
    }
  }, [hasAgreed, user, returnUrl]);

  if (error) {
    return (
      <Message color="red">
        <MessageHeader>{error}</MessageHeader>

        <Divider hidden />
        <MessageContent>
          <Button size="small" onClick={() => navigate("/login")}>
            Return to login
          </Button>
        </MessageContent>
      </Message>
    );
  }

  if (!organisation) {
    return <Loader />;
  }

  return (
    <div
      style={{ maxHeight: "69vh", display: "flex", flexDirection: "column" }}
    >
      <div style={{ overflowY: "auto", paddingRight: 8 }}>
        <H3>
          {t("entry.terms.title")}
          <Subheader>
            {user
              ? t("entry.terms.description")
              : t("entry.terms.newUserDescription")}
          </Subheader>
        </H3>
        <TermsCheckbox
          organisation={organisation}
          agreed={hasAgreed}
          onAgree={(agreed) => setHasAgreed(agreed)}
          agreedCustomTerms={agreedCustomTerms}
          onAgreeCustomTerms={(agreed) => setAgreedCustomTerms(agreed)}
        />
      </div>
      <Divider hidden />
      <SettingActions>
        <Button
          onClick={onAgree}
          loading={loading}
          disabled={requiresTermsAgreement || requiresCustomTermsAgreement}
          color="primary"
        >
          Continue
        </Button>
      </SettingActions>
    </div>
  );
};

export default AgreeToTerms;
